import React, { useEffect, useMemo } from 'react';
import { Button, Drawer, Form, Input, message, Select } from 'antd';
import { mapRegions } from 'utils';
import { UGDService } from 'services/UGDService';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { onlyNumberRule, requiredRule } from 'utils/forms/validationRules';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';

function UGDForm({ isActive, onClose, onSuccess, initialValues, regions }) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;

  const regionsList = useMemo(() => {
    return mapRegions(regions);
  }, [regions]);

  useEffect(() => {
    if (initialValues) {
      const { number, name, requisite, region } = initialValues;

      form.setFieldsValue({
        number,
        name,
        requisite,
        region_id: region?.id ? region.id : null,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      id: initialValues?.id,
    };

    if (isEditing) {
      handleUpdate(payload, initialValues.id);
    } else {
      handleCreate(payload);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await UGDService.createUGD(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await UGDService.editUGD(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onClose();
    onSuccess();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование УГД' : 'Создание УГД'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item
          name="number"
          label="Код НУ для зачисления в бюджет"
          rules={[requiredRule]}
        >
          <Input maxLength={6} placeholder="Код НУ для зачисления в бюджет" />
        </Form.Item>

        <Form.Item
          name="name"
          label="Наименование бенефициара"
          rules={[requiredRule]}
        >
          <Input placeholder="Введите наименование бенефициара" allowClear />
        </Form.Item>

        <Form.Item
          name="requisite"
          label="БИН бенефициара"
          rules={[requiredRule, onlyNumberRule]}
        >
          <Input
            maxLength={12}
            placeholder="Введите БИН бенефициара"
            allowClear
          />
        </Form.Item>

        <Form.Item name="region_id" label="Город" rules={[requiredRule]}>
          <Select
            allowClear
            placeholder="Город"
            showSearch
            optionFilterProp="children"
          >
            {regionsList.map(({ id, name }) => (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { UGDForm };
